import { type Aperture, type ComponentEffect, toRender } from "refract-rxjs";
import { combineLatest } from "rxjs";
import { filter, map, startWith } from "rxjs/operators";

import { type MetricField } from "../../gql-generated/types";
import { type GetOrganicStatsQuery } from "../api/queries";
import { generateDefaultMetrics } from "../generators";
import { type InsightsMetricsMap, type StatsItem } from "../models";

import { makeTotalMetricsMap } from "./makeTotalMetricsMap";
import { mapTotalMetricsData } from "./mapTotalMetricsData";

export interface LensOrganicStatsTotalEffectInput {
    data: GetOrganicStatsQuery | undefined;
    loading: boolean;
    dataPrevious: GetOrganicStatsQuery | undefined;
    loadingPrevious: boolean;
}

export interface LensOrganicStatsTotalEffectOutput {
    totalMetrics?: InsightsMetricsMap<StatsItem>;
}

export const makeLensOrganicStatsTotalEffect =
    (
        metricFields: MetricField[],
    ): Aperture<LensOrganicStatsTotalEffectInput, ComponentEffect<LensOrganicStatsTotalEffectOutput>> =>
    ({ observe }) => {
        const data$ = observe<GetOrganicStatsQuery | undefined>("data");
        const loading$ = observe<boolean>("loading");
        const dataPrevious$ = observe<GetOrganicStatsQuery | undefined>("dataPrevious");
        const loadingPrevious$ = observe<boolean>("loadingPrevious");

        return combineLatest([data$, dataPrevious$, loading$, loadingPrevious$]).pipe(
            filter((values): values is [GetOrganicStatsQuery, GetOrganicStatsQuery | undefined, boolean, boolean] => {
                const [data, _, loading, loadingPrevious] = values;
                return data !== undefined && !loading && !loadingPrevious;
            }),
            map(([data, dataPrevious]) => {
                const totalMap = makeTotalMetricsMap(data.getMyLensesOrganicStats.responseEntryList, metricFields);
                const previousTotalMap =
                    dataPrevious &&
                    makeTotalMetricsMap(dataPrevious.getMyLensesOrganicStats.responseEntryList, metricFields);

                return mapTotalMetricsData(metricFields, totalMap, previousTotalMap);
            }),
            startWith({
                totalMetrics: generateDefaultMetrics(metricFields),
            }),
            map(({ totalMetrics }) => toRender<LensOrganicStatsTotalEffectOutput>({ totalMetrics })),
        );
    };
